/**
 * Ontology Graph Manager Component
 * P0.2 - Manage ontology nodes and edges
 * P1.1 - Extended with Proposals Queue and Versioning
 * P1.2 - Extended with AKU v2 (Claim-based Knowledge Units)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Network, 
  CircleDot,
  ArrowRight,
  Plus,
  RefreshCw,
  Search,
  CheckCircle,
  Clock,
  XCircle,
  Trash2,
  Eye,
  ChevronDown,
  ChevronUp,
  Link2,
  Merge,
  AlertTriangle,
  ListChecks,
  GitBranch,
  Send,
  BookOpen
} from 'lucide-react';
import { toast } from 'sonner';
import { adminFetchHeaders } from '@/hooks/useAdminFetch';

// P1.1: Import new components
import ProposalsQueue from './ProposalsQueue';
import OntologyVersionsManager from './OntologyVersionsManager';
// P1.2: Import AKU v2 component
import AKUV2Manager from './AKUV2Manager';

const API = window.location.origin;

const getAuthHeaders = () => adminFetchHeaders(true);

// Node type options
const NODE_TYPES = [
  { value: 'CONCEPT', label: 'Konzept', color: 'bg-blue-100 text-blue-800' },
  { value: 'TERM', label: 'Begriff', color: 'bg-green-100 text-green-800' },
  { value: 'PRINCIPLE', label: 'Prinzip', color: 'bg-purple-100 text-purple-800' },
  { value: 'PRACTICE', label: 'Praxis', color: 'bg-orange-100 text-orange-800' },
  { value: 'ROLE', label: 'Rolle', color: 'bg-pink-100 text-pink-800' },
  { value: 'ARTIFACT', label: 'Artefakt', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'EVENT', label: 'Event', color: 'bg-cyan-100 text-cyan-800' },
  { value: 'PROCESS', label: 'Prozess', color: 'bg-indigo-100 text-indigo-800' },
  { value: 'DOMAIN', label: 'Domäne', color: 'bg-gray-100 text-gray-800' }
];

// Edge type options
const EDGE_TYPES = [
  { value: 'IS_A', label: 'ist ein' },
  { value: 'PART_OF', label: 'Teil von' },
  { value: 'RELATED_TO', label: 'verwandt mit' },
  { value: 'DEFINES', label: 'definiert' },
  { value: 'REQUIRES', label: 'erfordert' },
  { value: 'PRODUCES', label: 'produziert' },
  { value: 'USES', label: 'verwendet' },
  { value: 'CONTRADICTS', label: 'widerspricht' },
  { value: 'SUPERSEDES', label: 'ersetzt' },
  { value: 'SYNONYM_OF', label: 'Synonym von' }
];

// Status colors - P1.1 Extended
const STATUS_COLORS = {
  DRAFT: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  PROPOSED: 'bg-blue-100 text-blue-800 border-blue-300',
  REVIEW: 'bg-blue-100 text-blue-800 border-blue-300',
  APPROVED: 'bg-green-100 text-green-800 border-green-300',
  REJECTED: 'bg-red-100 text-red-800 border-red-300',
  DEPRECATED: 'bg-red-100 text-red-800 border-red-300',
  MERGED: 'bg-purple-100 text-purple-800 border-purple-300'
};

const OntologyManager = ({ isActive = false, examProfileId = '' }) => {
  const [stats, setStats] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [activeTab, setActiveTab] = useState('nodes');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  
  // P1.1: Main view tabs - Default to 'versions' (Active Released view)
  const [mainView, setMainView] = useState('versions'); // 'graph', 'proposals', 'versions', 'akuv2'
  
  // Advanced mode toggle for create buttons
  const [showAdvancedMode, setShowAdvancedMode] = useState(false);
  
  // Create node dialog
  const [showCreateNode, setShowCreateNode] = useState(false);
  const [newNode, setNewNode] = useState({
    node_type: 'CONCEPT',
    canonical_name: '',
    aliases: '',
    definition: '',
    domain: '',
    exam_ids: []
  });
  
  // Create edge dialog
  const [showCreateEdge, setShowCreateEdge] = useState(false);
  const [newEdge, setNewEdge] = useState({
    source_node_id: '',
    target_node_id: '',
    edge_type: 'RELATED_TO',
    label: ''
  });
  
  // Detail view
  const [selectedNode, setSelectedNode] = useState(null);
  const [expandedNode, setExpandedNode] = useState(null);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/ontology/stats/overview`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  // Fetch nodes
  const fetchNodes = useCallback(async () => {
    try {
      let url = `${API}/api/ontology/nodes?limit=200`;
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
      if (filterType) url += `&node_type=${filterType}`;
      if (filterStatus) url += `&status=${filterStatus}`;
      
      const response = await fetch(url, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setNodes(data);
      }
    } catch (error) {
      console.error('Error fetching nodes:', error);
    }
  }, [searchTerm, filterType, filterStatus]);

  // Fetch edges
  const fetchEdges = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/ontology/edges?limit=200`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setEdges(data);
      }
    } catch (error) {
      console.error('Error fetching edges:', error);
    }
  }, []);

  // Lazy load data only when tab is active
  useEffect(() => {
    const loadData = async () => {
      if (!isActive || initialLoadDone) return;
      setLoading(true);
      await Promise.all([fetchStats(), fetchNodes(), fetchEdges()]);
      setLoading(false);
      setInitialLoadDone(true);
    };
    loadData();
  }, [isActive, initialLoadDone, fetchStats, fetchNodes, fetchEdges]);

  // Create node
  const handleCreateNode = async () => {
    try {
      const payload = {
        ...newNode,
        aliases: newNode.aliases ? newNode.aliases.split(',').map(a => a.trim()).filter(a => a) : [],
        exam_ids: newNode.exam_ids || []
      };
      
      const response = await fetch(`${API}/api/ontology/nodes`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        if (data.existing_node_id) {
          toast.info(`Node existiert bereits: ${data.existing_node_id}`);
        } else {
          toast.success(`Node "${newNode.canonical_name}" erstellt`);
        }
        setShowCreateNode(false);
        setNewNode({ node_type: 'CONCEPT', canonical_name: '', aliases: '', definition: '', domain: '', exam_ids: [] });
        await Promise.all([fetchStats(), fetchNodes()]);
      } else {
        toast.error(data.detail || 'Fehler beim Erstellen');
      }
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    }
  };

  // Create edge
  const handleCreateEdge = async () => {
    try {
      const response = await fetch(`${API}/api/ontology/edges`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newEdge)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        if (data.existing_edge_id) {
          toast.info(`Edge existiert bereits: ${data.existing_edge_id}`);
        } else {
          toast.success('Beziehung erstellt');
        }
        setShowCreateEdge(false);
        setNewEdge({ source_node_id: '', target_node_id: '', edge_type: 'RELATED_TO', label: '' });
        await Promise.all([fetchStats(), fetchEdges()]);
      } else {
        toast.error(data.detail || 'Fehler beim Erstellen');
      }
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    }
  };

  // Approve node
  const handleApproveNode = async (nodeId) => {
    try {
      const response = await fetch(`${API}/api/ontology/nodes/${nodeId}/approve`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: 'APPROVED' })
      });
      
      if (response.ok) {
        toast.success('Node genehmigt');
        await Promise.all([fetchStats(), fetchNodes()]);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler');
      }
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    }
  };

  // Delete node
  const handleDeleteNode = async (nodeId) => {
    if (!window.confirm('Node und alle verbundenen Edges wirklich löschen?')) return;
    
    try {
      const response = await fetch(`${API}/api/ontology/nodes/${nodeId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Node gelöscht (${data.edges_deleted} Edges entfernt)`);
        await Promise.all([fetchStats(), fetchNodes(), fetchEdges()]);
      }
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    }
  };

  // Delete edge
  const handleDeleteEdge = async (edgeId) => {
    if (!window.confirm('Beziehung wirklich löschen?')) return;
    
    try {
      const response = await fetch(`${API}/api/ontology/edges/${edgeId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('Beziehung gelöscht');
        await Promise.all([fetchStats(), fetchEdges()]);
      }
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    }
  };

  // Get node name by ID
  const getNodeName = (nodeId) => {
    const node = nodes.find(n => n.node_id === nodeId);
    return node ? node.canonical_name : nodeId;
  };

  if (loading && mainView === 'graph') {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-purple-500" />
        <span className="ml-2">Lade Ontology Graph...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* P1.1: Main View Tabs */}
      <div className="border-b">
        <div className="flex gap-1">
          <button
            className={`px-4 py-2 flex items-center gap-2 border-b-2 transition-colors ${
              mainView === 'graph' 
                ? 'border-purple-600 text-purple-600 font-medium' 
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
            onClick={() => setMainView('graph')}
            data-testid="tab-graph"
          >
            <Network className="w-4 h-4" />
            Graph
          </button>
          <button
            className={`px-4 py-2 flex items-center gap-2 border-b-2 transition-colors ${
              mainView === 'proposals' 
                ? 'border-blue-600 text-blue-600 font-medium' 
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
            onClick={() => setMainView('proposals')}
            data-testid="tab-proposals"
          >
            <ListChecks className="w-4 h-4" />
            Proposals Queue
          </button>
          <button
            className={`px-4 py-2 flex items-center gap-2 border-b-2 transition-colors ${
              mainView === 'versions' 
                ? 'border-green-600 text-green-600 font-medium' 
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
            onClick={() => setMainView('versions')}
            data-testid="tab-versions"
          >
            <GitBranch className="w-4 h-4" />
            Versioning
          </button>
          <button
            className={`px-4 py-2 flex items-center gap-2 border-b-2 transition-colors ${
              mainView === 'akuv2' 
                ? 'border-orange-600 text-orange-600 font-medium' 
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
            onClick={() => setMainView('akuv2')}
            data-testid="tab-akuv2"
          >
            <BookOpen className="w-4 h-4" />
            AKU v2
          </button>
        </div>
      </div>

      {/* P1.1 + P1.2: Render based on main view */}
      {mainView === 'proposals' && <ProposalsQueue examProfileId={examProfileId} />}
      {mainView === 'versions' && <OntologyVersionsManager />}
      {mainView === 'akuv2' && <AKUV2Manager />}
      
      {/* Original Graph View */}
      {mainView === 'graph' && (
        <>
          {/* Header */}
          {/* Read-only notice for graph view */}
          <Card className="border-yellow-200 bg-yellow-50 mb-4">
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-yellow-800">
                    Staging/Graph-Ansicht - Nur für fortgeschrittene Benutzer
                  </p>
                  <p className="text-sm text-yellow-700 mt-1">
                    Konzepte werden über den <strong>Proposals Queue</strong> Tab erstellt und kuratiert.
                    Manuelle Erstellung nur im Advanced-Modus möglich.
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAdvancedMode(!showAdvancedMode)}
                >
                  {showAdvancedMode ? 'Advanced ausblenden' : 'Advanced anzeigen'}
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <Network className="w-6 h-6 text-purple-600" />
                Ontology Graph (Staging)
              </h2>
              <p className="text-gray-600 mt-1">
                Konzepte, Begriffe und deren Beziehungen
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => Promise.all([fetchStats(), fetchNodes(), fetchEdges()])}
                data-testid="refresh-ontology"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Aktualisieren
              </Button>
              
              {/* Create buttons only visible in Advanced Mode */}
              {showAdvancedMode && (
                <>
                  <Button onClick={() => setShowCreateNode(true)} data-testid="create-node-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Node erstellen
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setShowCreateEdge(true)}
                    data-testid="create-edge-btn"
                  >
                    <Link2 className="w-4 h-4 mr-2" />
                    Beziehung
                  </Button>
                </>
              )}
            </div>
          </div>

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">{stats.total_nodes}</div>
              <div className="text-sm text-gray-600">Nodes</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">{stats.total_edges}</div>
              <div className="text-sm text-gray-600">Edges</div>
            </CardContent>
          </Card>
          
          <Card className="border-yellow-200 bg-yellow-50">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-yellow-700">
                {stats.pending_approval}
              </div>
              <div className="text-sm text-yellow-600 flex items-center gap-1">
                <Clock className="w-4 h-4" />
                Zur Prüfung
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-1">
                {Object.entries(stats.nodes_by_type || {}).slice(0, 3).map(([type, count]) => (
                  <Badge key={type} variant="outline" className="text-xs">
                    {count} {type}
                  </Badge>
                ))}
              </div>
              <div className="text-sm text-gray-600 mt-2">Nach Typ</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-1">
                {Object.entries(stats.nodes_by_domain || {}).slice(0, 3).map(([domain, count]) => (
                  <Badge key={domain} variant="outline" className="text-xs">
                    {count} {domain}
                  </Badge>
                ))}
              </div>
              <div className="text-sm text-gray-600 mt-2">Nach Domäne</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="nodes" data-testid="tab-nodes">
            <CircleDot className="w-4 h-4 mr-1" />
            Nodes ({nodes.length})
          </TabsTrigger>
          <TabsTrigger value="edges" data-testid="tab-edges">
            <ArrowRight className="w-4 h-4 mr-1" />
            Edges ({edges.length})
          </TabsTrigger>
        </TabsList>

        {/* Nodes Tab */}
        <TabsContent value="nodes" className="space-y-4">
          {/* Filters */}
          <div className="flex gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Suchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="search-nodes"
                />
              </div>
            </div>
            <Select value={filterType || "ALL"} onValueChange={(v) => setFilterType(v === "ALL" ? "" : v)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Alle Typen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Alle Typen</SelectItem>
                {NODE_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus || "ALL"} onValueChange={(v) => setFilterStatus(v === "ALL" ? "" : v)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Alle Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Alle Status</SelectItem>
                <SelectItem value="DRAFT">Draft</SelectItem>
                <SelectItem value="PROPOSED">Proposed</SelectItem>
                <SelectItem value="APPROVED">Approved</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchNodes}>
              <Search className="w-4 h-4" />
            </Button>
          </div>

          {/* Nodes List */}
          <Card>
            <CardContent className="pt-4">
              {/* P2: Header with count */}
              <div className="flex items-center justify-between mb-3 pb-2 border-b">
                <span className="text-sm font-medium text-gray-600">
                  {nodes.length} Nodes
                </span>
              </div>
              {nodes.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Network className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Keine Nodes vorhanden.</p>
                  <p className="text-sm mt-2">Erstellen Sie den ersten Node im Ontology Graph.</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2" data-testid="nodes-list">
                  {nodes.map((node) => {
                    const typeConfig = NODE_TYPES.find(t => t.value === node.node_type) || NODE_TYPES[0];
                    const isExpanded = expandedNode === node.node_id;
                    
                    return (
                      <div 
                        key={node.node_id}
                        className="border rounded-lg p-3 hover:bg-gray-50"
                        data-testid={`node-${node.node_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Badge className={typeConfig.color}>
                              {typeConfig.label}
                            </Badge>
                            <div>
                              <div className="font-medium">{node.canonical_name}</div>
                              <div className="text-xs text-gray-500">
                                {node.node_id} {node.domain && `• ${node.domain}`}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Badge className={STATUS_COLORS[node.status] || ''}>
                              {node.status}
                            </Badge>
                            
                            {(node.status === 'DRAFT' || node.status === 'PROPOSED') && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-green-600"
                                onClick={() => handleApproveNode(node.node_id)}
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                            )}
                            
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setExpandedNode(isExpanded ? null : node.node_id)}
                            >
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </Button>
                            
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600"
                              onClick={() => handleDeleteNode(node.node_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        
                        {isExpanded && (
                          <div className="mt-3 pt-3 border-t text-sm space-y-2">
                            {node.definition && (
                              <div>
                                <span className="text-gray-500">Definition:</span>
                                <p className="mt-1">{node.definition}</p>
                              </div>
                            )}
                            {node.aliases && node.aliases.length > 0 && (
                              <div>
                                <span className="text-gray-500">Aliase:</span>
                                <span className="ml-2">{node.aliases.join(', ')}</span>
                              </div>
                            )}
                            {node.evidence && node.evidence.length > 0 && (
                              <div className="flex items-center gap-1 text-green-600">
                                <CheckCircle className="w-4 h-4" />
                                <span>{node.evidence.length} Evidence-Quellen</span>
                              </div>
                            )}
                            <div className="text-xs text-gray-400">
                              Erstellt: {new Date(node.created_at).toLocaleString('de-DE')}
                              {node.created_by && ` von ${node.created_by}`}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Edges Tab */}
        <TabsContent value="edges" className="space-y-4">
          <Card>
            <CardContent className="pt-4">
              {/* P2: Header with count */}
              <div className="flex items-center justify-between mb-3 pb-2 border-b">
                <span className="text-sm font-medium text-gray-600">
                  {edges.length} Edges
                </span>
              </div>
              {edges.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <ArrowRight className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Keine Beziehungen vorhanden.</p>
                  <p className="text-sm mt-2">Erstellen Sie eine Beziehung zwischen Nodes.</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2" data-testid="edges-list">
                  {edges.map((edge) => {
                    const edgeTypeConfig = EDGE_TYPES.find(t => t.value === edge.edge_type) || { label: edge.edge_type };
                    
                    return (
                      <div 
                        key={edge.edge_id}
                        className="border rounded-lg p-3 hover:bg-gray-50"
                        data-testid={`edge-${edge.edge_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline">{getNodeName(edge.source_node_id)}</Badge>
                            <span className="text-purple-600 font-medium text-sm">
                              —[ {edgeTypeConfig.label} ]→
                            </span>
                            <Badge variant="outline">{getNodeName(edge.target_node_id)}</Badge>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Badge className={STATUS_COLORS[edge.status] || ''}>
                              {edge.status}
                            </Badge>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600"
                              onClick={() => handleDeleteEdge(edge.edge_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        {edge.label && (
                          <div className="text-sm text-gray-500 mt-1">{edge.label}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      </>
      )}

      {/* Create Node Dialog */}
      <Dialog open={showCreateNode} onOpenChange={setShowCreateNode}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Node erstellen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Typ</Label>
              <Select 
                value={newNode.node_type} 
                onValueChange={(v) => setNewNode({...newNode, node_type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {NODE_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Name *</Label>
              <Input
                value={newNode.canonical_name}
                onChange={(e) => setNewNode({...newNode, canonical_name: e.target.value})}
                placeholder="z.B. Sprint, Product Backlog"
                data-testid="node-name-input"
              />
            </div>
            
            <div>
              <Label>Aliase (kommagetrennt)</Label>
              <Input
                value={newNode.aliases}
                onChange={(e) => setNewNode({...newNode, aliases: e.target.value})}
                placeholder="z.B. Iteration, Development Cycle"
              />
            </div>
            
            <div>
              <Label>Definition</Label>
              <Textarea
                value={newNode.definition}
                onChange={(e) => setNewNode({...newNode, definition: e.target.value})}
                placeholder="Die offizielle Definition..."
                rows={3}
              />
            </div>
            
            <div>
              <Label>Domäne</Label>
              <Input
                value={newNode.domain}
                onChange={(e) => setNewNode({...newNode, domain: e.target.value})}
                placeholder="z.B. Scrum, PMBOK, SAFe"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateNode(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleCreateNode}
              disabled={!newNode.canonical_name}
              data-testid="submit-create-node"
            >
              Erstellen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Edge Dialog */}
      <Dialog open={showCreateEdge} onOpenChange={setShowCreateEdge}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Beziehung erstellen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Von Node</Label>
              <Select 
                value={newEdge.source_node_id} 
                onValueChange={(v) => setNewEdge({...newEdge, source_node_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Node auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  {nodes.map(n => (
                    <SelectItem key={n.node_id} value={n.node_id}>
                      {n.canonical_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Beziehungstyp</Label>
              <Select 
                value={newEdge.edge_type} 
                onValueChange={(v) => setNewEdge({...newEdge, edge_type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EDGE_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Zu Node</Label>
              <Select 
                value={newEdge.target_node_id} 
                onValueChange={(v) => setNewEdge({...newEdge, target_node_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Node auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  {nodes.map(n => (
                    <SelectItem key={n.node_id} value={n.node_id}>
                      {n.canonical_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Label (optional)</Label>
              <Input
                value={newEdge.label}
                onChange={(e) => setNewEdge({...newEdge, label: e.target.value})}
                placeholder="Beschreibung der Beziehung..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateEdge(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleCreateEdge}
              disabled={!newEdge.source_node_id || !newEdge.target_node_id}
              data-testid="submit-create-edge"
            >
              Erstellen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OntologyManager;
